import intl from 'react-intl-universal';
import { isEqual } from 'lodash';
import { TaskBundle } from 'utils/customTypes';
import {
  TaskTemplate,
  TaskTemplateDependencyEdge,
} from 'utils/types/taskTemplate';
import { TASK_BUNDLE_OPERATION_MODES } from 'utils/constants/taskBundles';
import { PATHS } from 'utils/constants';
import {
  TaskBundleDraft,
  TaskBundleMode,
  TaskTemplateDraftDependencies,
} from './types';

interface CreateInitialTaskBundleDraftProps {
  activeBundle: TaskBundle;
  activeBundleTemplates: TaskTemplate[];
  mode: TaskBundleMode;
  dependencyEdges: TaskTemplateDependencyEdge[];
}

const isCloneMode = (mode: TaskBundleMode) => {
  return mode === TASK_BUNDLE_OPERATION_MODES.CLONE;
};

const isCreateMode = (mode: TaskBundleMode) => {
  return mode === TASK_BUNDLE_OPERATION_MODES.CREATE;
};

export const isEditingMode = (mode: TaskBundleMode) => {
  return mode === TASK_BUNDLE_OPERATION_MODES.EDIT;
};

const getBundleName = (activeBundle: TaskBundle, mode: TaskBundleMode) => {
  if (isCloneMode(mode)) {
    return intl.get(
      'SETTINGS_PAGE.TASK_BUNDLE_PAGE.OVERVIEW_SECTION.TITLE_CLONE',
      {
        taskBundleName: activeBundle.name,
      }
    );
  }
  return activeBundle.name;
};

function buildTemplateDependencies(
  dependencyEdges: TaskTemplateDependencyEdge[]
): Map<string, TaskTemplateDraftDependencies> {
  const templateDependencies = new Map<string, TaskTemplateDraftDependencies>();

  dependencyEdges.forEach((edge) => {
    if (!templateDependencies.has(edge.dependentId)) {
      templateDependencies.set(edge.dependentId, {
        predecessors: [],
        successors: [],
      });
    }

    if (!templateDependencies.has(edge.dependencyId)) {
      templateDependencies.set(edge.dependencyId, {
        predecessors: [],
        successors: [],
      });
    }

    const dependentDeps = templateDependencies.get(edge.dependentId)!;
    const dependencyDeps = templateDependencies.get(edge.dependencyId)!;

    dependentDeps.predecessors.push(edge.dependencyId);
    dependencyDeps.successors.push(edge.dependentId);
  });

  return templateDependencies;
}

function adaptTemplateToTaskTemplateDraft(
  template: TaskTemplate,
  templateDependencies?: Map<
    string,
    { predecessors: string[]; successors: string[] }
  >
) {
  const newId = template.id;
  const savedDependencies = templateDependencies?.get(template.id);

  return {
    id: newId,
    name: template.name,
    description: template.description ?? '',
    estimated_hours: template.estimated_hours ?? '',
    type: template.type ?? '',
    start_day: template.start_day ?? '',
    duration: template.duration ?? '',
    dependencies: savedDependencies ?? {
      predecessors: [],
      successors: [],
    },
  };
}

export const createInitialTaskBundleDraft: (
  params: CreateInitialTaskBundleDraftProps
) => TaskBundleDraft = ({
  activeBundle,
  activeBundleTemplates,
  mode,
  dependencyEdges,
}) => {
  if (isCreateMode(mode)) {
    return {
      bundle: { id: undefined, name: '', description: '' },
      templates: [],
    };
  }

  const templateDependencies = buildTemplateDependencies(dependencyEdges);

  return {
    bundle: {
      id: isCloneMode(mode) ? undefined : activeBundle.id,
      name: getBundleName(activeBundle, mode),
      description: activeBundle.description ?? '',
      taskBundleCreator: activeBundle.taskBundleCreator ?? undefined,
    },
    templates: activeBundleTemplates.map((template) =>
      adaptTemplateToTaskTemplateDraft(template, templateDependencies)
    ),
  };
};

export const mapPathToOperationMode = new Map<
  string,
  TASK_BUNDLE_OPERATION_MODES
>([
  [PATHS.NEW_TASK_BUNDLE_PAGE, TASK_BUNDLE_OPERATION_MODES.CREATE],
  [PATHS.CLONE_TASK_BUNDLE_PAGE, TASK_BUNDLE_OPERATION_MODES.CLONE],
  [PATHS.EDIT_TASK_BUNDLE_PAGE, TASK_BUNDLE_OPERATION_MODES.EDIT],
]);

export const getPageTitle = (mode: TaskBundleMode, bundleName: string) => {
  if (isCreateMode(mode)) {
    return intl.get('SETTINGS_PAGE.TASK_BUNDLE_PAGE.ADD_TASK_BUNDLE');
  }
  if (isCloneMode(mode)) {
    return intl.get('SETTINGS_PAGE.TASK_BUNDLE_PAGE.DUPLICATE_TASK_BUNDLE');
  }
  return bundleName;
};

export function shouldResetDraft(
  draft: TaskBundleDraft,
  newDraft: TaskBundleDraft
) {
  // Check if bundle changed (excluding dependencies)
  if (!isEqual(draft.bundle, newDraft.bundle)) {
    return true;
  }

  // Check if template structure/metadata changed (excluding dependencies)
  const oldTemplatesWithoutDeps = draft.templates.map((t) => ({
    ...t,
    dependencies: undefined,
  }));
  const newTemplatesWithoutDeps = newDraft.templates.map((t) => ({
    ...t,
    dependencies: undefined,
  }));

  if (!isEqual(oldTemplatesWithoutDeps, newTemplatesWithoutDeps)) {
    return true;
  }

  return false;
}
